import { memo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, User, Calendar, CheckCircle, Shield, Star, Building2,
  FileText, CreditCard, Settings, Bell
} from 'lucide-react';

const stepCardClass = 'home-surface-interactive p-6 rounded-2xl';
const stepBadgeClass =
  'w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold';
const stepIconClass = 'h-6 w-6 text-primary-600';

const HowItWorksSection = memo(() => {
  return (
    <section 
      id="how-it-works" 
      className="bg-white py-20 sm:py-24"
      aria-labelledby="how-it-works-heading"
      role="region"
    >
      <div className="container mx-auto w-full max-w-screen-2xl px-4 sm:px-6 lg:px-8">
        <header className="mx-auto mb-14 max-w-3xl text-center sm:mb-16">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            How it works
          </p>
          <h2
            id="how-it-works-heading"
            className="text-balance text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl md:text-[2.25rem] md:leading-snug"
          >
            How our rental concierge works
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-neutral-600">
            A simple, guided journey from telling us what you need to moving into a home that fits your life.
          </p>
        </header>

        {/* Renter Features */}
        <section 
          className="mb-16"
          aria-labelledby="renter-features-heading"
          role="region"
        >
          <header className="text-center mb-8">
            <div
              className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4"
              aria-hidden="true"
            >
              <User className="h-8 w-8 text-primary-600" />
            </div>
            <h3
              id="renter-features-heading"
              className="text-2xl font-semibold text-neutral-900 mb-2"
            >
              For renters
            </h3>
            <p className="text-neutral-600">
              A dedicated specialist guiding you from first inquiry to move‑in.
            </p>
          </header>

          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            role="list"
            aria-label="Renter features - first set"
          >
            {/* Feature 1 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 1">1</div>
                <Search className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-2">Tell us what you need</h4>
              <p className="text-sm text-neutral-600">
                Share your ideal location, budget, and must‑haves in our “Find me a home” form.
              </p>
              <div className="mt-3 flex items-center text-xs text-primary-600">
                <Shield className="h-3 w-3 mr-1" aria-hidden="true" />
                <span>We only work with verified homes</span>
              </div>
            </article>

            {/* Feature 2 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 2">2</div>
                <User className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-2">We curate options for you</h4>
              <p className="text-sm text-neutral-600">
                A local specialist hand‑picks rentals that fit your brief and budget, not just a filter.
              </p>
              <div className="mt-3 flex items-center text-xs text-primary-600">
                <Star className="h-3 w-3 mr-1" aria-hidden="true" />
                <span>Curated by local experts</span>
              </div>
            </article>

            {/* Feature 3 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 3">3</div>
                <Calendar className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-2">Viewings on your schedule</h4>
              <p className="text-sm text-neutral-600">
                We help coordinate viewing schedules so you only visit homes that are worth your time.
              </p>
            </article>

            {/* Feature 4 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 4">4</div>
                <CheckCircle className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-900 mb-2">Decide and move in with support</h4>
              <p className="text-sm text-neutral-600">
                From application to move‑in, we’re here to help you finalize details and feel confident.
              </p>
            </article>
          </div>

          <div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6"
            role="list"
            aria-label="Renter features - second set"
          >
            {/* Feature 5 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 5">5</div>
                <FileText className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-800 mb-2">Contract Generation</h4>
              <p className="text-sm text-neutral-600">
                Auto-generated digital contract with all terms. Electronic signatures for both parties.
              </p>
              <div className="mt-3 flex items-center text-xs text-primary-600">
                <Shield className="h-3 w-3 mr-1" aria-hidden="true" />
                <span>Legally binding</span>
              </div>
            </article>

            {/* Feature 6 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 6">6</div>
                <CreditCard className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-800 mb-2">Secure Payments</h4>
              <p className="text-sm text-neutral-600">
                Pay deposit, first month's rent, and ongoing rent through GCash, PayMaya, cards, or bank transfer.
              </p>
              <div className="mt-3 flex items-center text-xs text-primary-600">
                <CheckCircle className="h-3 w-3 mr-1" aria-hidden="true" />
                <span>Auto-receipts</span>
              </div>
            </article>

            {/* Feature 7 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 7">7</div>
                <Settings className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-800 mb-2">Maintenance Requests</h4>
              <p className="text-sm text-neutral-600">
                File maintenance requests, upload photos/videos, track status, and rate service quality.
              </p>
              <div className="mt-3 flex items-center text-xs text-primary-600">
                <Bell className="h-3 w-3 mr-1" aria-hidden="true" />
                <span>Real-time updates</span>
              </div>
            </article>

            {/* Feature 8 */}
            <article className={stepCardClass} role="listitem">
              <div className="flex items-center justify-between mb-4">
                <div className={stepBadgeClass} aria-label="Step 8">8</div>
                <Calendar className={stepIconClass} aria-hidden="true" />
              </div>
              <h4 className="font-semibold text-neutral-800 mb-2">Ongoing Management</h4>
              <p className="text-sm text-neutral-600">
                Track rent due dates, lease status, renewals, and any agreements with your landlord.
              </p>
              <div className="mt-3 flex items-center text-xs text-primary-600">
                <CheckCircle className="h-3 w-3 mr-1" aria-hidden="true" />
                <span>Complete history</span>
              </div>
            </article>
          </div>
        </section>

        {/* Agent & Landlord Journey */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* For Agents */}
          <section
            className="home-surface-interactive p-8 rounded-2xl"
            aria-labelledby="agent-features-heading"
            role="region"
          >
            <header className="text-center mb-6">
              <div
                className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4"
                aria-hidden="true"
              >
                <User className="h-8 w-8 text-primary-600" />
              </div>
              <h3
                id="agent-features-heading"
                className="text-2xl font-semibold text-neutral-900 mb-2"
              >
                For real estate agents
              </h3>
              <p className="text-neutral-600">Grow your business with our comprehensive tools</p>
            </header>
            
            <div 
              className="space-y-4"
              role="list"
              aria-label="Real estate agent features"
            >
              <div className="flex items-start space-x-3" role="listitem">
                <div
                  className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                  aria-label="Agent step 1"
                >
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">List Properties</h4>
                  <p className="text-sm text-neutral-600">Upload photos, set pricing, and manage availability</p>
                </div>
              </div>
              <div className="flex items-start space-x-3" role="listitem">
                <div
                  className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                  aria-label="Agent step 2"
                >
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">Manage Applications</h4>
                  <p className="text-sm text-neutral-600">Review tenant applications and process approvals</p>
                </div>
              </div>
              <div className="flex items-start space-x-3" role="listitem">
                <div
                  className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                  aria-label="Agent step 3"
                >
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">Track Performance</h4>
                  <p className="text-sm text-neutral-600">Monitor success metrics and client satisfaction</p>
                </div>
              </div>
            </div>

            <footer className="mt-6 text-center">
              <Link
                to="/register-enhanced"
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-describedby="join-agent-description"
              >
                <User className="h-4 w-4 mr-2" aria-hidden="true" />
                Join as Agent
              </Link>
              <div id="join-agent-description" className="sr-only">
                Register as a real estate agent to list properties and manage applications
              </div>
            </footer>
          </section>

          {/* For Landlords */}
          <section
            className="home-surface-interactive p-8 rounded-2xl"
            aria-labelledby="landlord-features-heading"
            role="region"
          >
            <header className="text-center mb-6">
              <div
                className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4"
                aria-hidden="true"
              >
                <Building2 className="h-8 w-8 text-primary-600" />
              </div>
              <h3
                id="landlord-features-heading"
                className="text-2xl font-semibold text-neutral-900 mb-2"
              >
                For property owners
              </h3>
              <p className="text-neutral-600">Maximize your rental income with smart management</p>
            </header>
            
            <div 
              className="space-y-4"
              role="list"
              aria-label="Property owner features"
            >
              <div className="flex items-start space-x-3" role="listitem">
                <div
                  className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                  aria-label="Owner step 1"
                >
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">List Your Properties</h4>
                  <p className="text-sm text-neutral-600">Showcase your properties to qualified tenants</p>
                </div>
              </div>
              <div className="flex items-start space-x-3" role="listitem">
                <div
                  className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                  aria-label="Owner step 2"
                >
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">Automated Rent Collection</h4>
                  <p className="text-sm text-neutral-600">Receive payments on time with automatic reminders</p>
                </div>
              </div>
              <div className="flex items-start space-x-3" role="listitem">
                <div
                  className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                  aria-label="Owner step 3"
                >
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">Maintenance Tracking</h4>
                  <p className="text-sm text-neutral-600">Handle tenant requests efficiently and maintain property value</p>
                </div>
              </div>
            </div>

            <footer className="mt-6 text-center">
              <Link
                to="/register-enhanced"
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-describedby="list-property-description"
              >
                <Building2 className="h-4 w-4 mr-2" aria-hidden="true" />
                List Your Property
              </Link>
              <div id="list-property-description" className="sr-only">
                Register as a property owner to list and manage your rental properties
              </div>
            </footer>
          </section>
        </div>

        {/* Trust Indicators */}
        <aside 
          className="mt-16 text-center"
          aria-labelledby="trust-indicators-heading"
          role="complementary"
        >
          <div className="sr-only">
            <h3 id="trust-indicators-heading">Trust and Security Features</h3>
          </div>
          <div
            className="inline-flex flex-wrap items-center justify-center gap-4 bg-neutral-50 rounded-2xl border border-neutral-100 px-6 sm:px-8 py-5 shadow-sm"
            role="list"
            aria-label="Platform security and trust features"
          >
            <div className="flex items-center space-x-2" role="listitem">
              <Shield className="h-6 w-6 text-primary-600" aria-hidden="true" />
              <span className="text-sm font-medium text-neutral-700">Verified properties</span>
            </div>
            <div className="flex items-center space-x-2" role="listitem">
              <CheckCircle className="h-6 w-6 text-primary-600" aria-hidden="true" />
              <span className="text-sm font-medium text-neutral-700">Concierge support</span>
            </div>
            <div className="flex items-center space-x-2" role="listitem">
              <Star className="h-6 w-6 text-primary-600" aria-hidden="true" />
              <span className="text-sm font-medium text-neutral-700">Trusted agents</span>
            </div>
          </div>
        </aside>

        {/* Get Started CTA */}
        <section 
          className="mt-12 text-center"
          aria-labelledby="get-started-heading"
          role="region"
        >
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white">
            <h3 
              id="get-started-heading"
              className="text-2xl font-bold mb-4"
            >
              Ready to have homes curated for you?
            </h3>
            <p className="text-lg text-primary-100 mb-6">
              Share what you’re looking for and let our team do the searching, shortlisting, and coordinating.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/#hero-search" 
                className="inline-flex items-center px-8 py-3 bg-white text-primary-700 font-semibold rounded-lg hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600"
                aria-describedby="concierge-cta-description"
              >
                <Search className="h-5 w-5 mr-2" aria-hidden="true" />
                Start “Find me a home”
              </Link>
              <div id="concierge-cta-description" className="sr-only">
                Jump to the Find me a home form to request curated home matches.
              </div>
              <Link 
                to="/register-enhanced" 
                className="inline-flex items-center px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary-600"
                aria-describedby="create-account-description"
              >
                <User className="h-5 w-5 mr-2" aria-hidden="true" />
                Create Account
              </Link>
              <div id="create-account-description" className="sr-only">
                Sign up for a free account to save favorite properties and apply for rentals
              </div>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
});

HowItWorksSection.displayName = 'HowItWorksSection';

export default HowItWorksSection;